/**
 * PocketFiler i18n (Internationalization) Configuration
 * 
 * This module provides:
 * - LanguageProvider: React context provider for language state
 * - useTranslation: Hook to access translation function and current language
 * - Translation files for all supported languages
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getStoredLanguage, setStoredLanguage, LANGUAGES } from '../lib/languages';

// Import all translation files
import en from './en';
import es from './es';
import zhCN from './zh-CN';
import fr from './fr';
import de from './de';
import pt from './pt';
import ar from './ar';
import hi from './hi';
import ja from './ja';
import ko from './ko';
import ru from './ru';
import vi from './vi';

// Translation resources map
const translations = {
  en,
  es,
  'zh-CN': zhCN,
  fr,
  de,
  pt,
  ar,
  hi,
  ja,
  ko,
  ru,
  // Fallback aliases
  zh: zhCN,
  vi,
};

// Create the Language Context
const LanguageContext = createContext(null);

/**
 * LanguageProvider
 * Provides language state and translation functions to the entire app
 */
export function LanguageProvider({ children }) {
  // Initialize with stored language or browser preference or default to English
  const [language, setLanguageState] = useState(() => {
    const stored = getStoredLanguage();
    if (stored && translations[stored]) return stored;
    
    // Try to detect browser language
    if (typeof navigator !== 'undefined') {
      const browserLang = navigator.language?.split('-')[0];
      if (browserLang && translations[browserLang]) return browserLang;
      // Special handling for Chinese
      if (navigator.language?.startsWith('zh')) return 'zh-CN';
    }
    
    return 'en';
  });

  // Get current translation dictionary
  const currentTranslations = translations[language] || translations.en;

  /**
   * Translate a key with optional interpolation
   * @param {string} key - Translation key (e.g., "landing.hero.title")
   * @param {object} params - Optional parameters for interpolation
   * @returns {string} Translated string or key if not found
   */
  const t = useCallback((key, params = {}) => {
    // Get translation from current language, fallback to English, then to key
    let translation = currentTranslations[key] || translations.en[key] || key;
    
    // Handle interpolation: replace {param} with actual values
    if (params && typeof translation === 'string') {
      Object.entries(params).forEach(([paramKey, value]) => {
        translation = translation.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), value);
      });
    }
    
    return translation;
  }, [currentTranslations]);

  /**
   * Change the current language
   * @param {string} langCode - Language code (e.g., "en", "es", "zh-CN")
   */
  const setLanguage = useCallback((langCode) => {
    console.log('i18n setLanguage called with:', langCode);
    // Normalize language code
    let normalizedCode = langCode;
    if (langCode === 'zh') normalizedCode = 'zh-CN';
    
    // Validate language exists
    if (!translations[normalizedCode]) {
      console.warn(`Language '${langCode}' not found, falling back to English`);
      normalizedCode = 'en';
    }
    
    console.log('i18n setting language to:', normalizedCode);
    setLanguageState(normalizedCode);
    setStoredLanguage(normalizedCode);
    
    // Update document direction for RTL languages
    const langConfig = LANGUAGES[normalizedCode];
    if (langConfig?.rtl) {
      document.documentElement.dir = 'rtl';
      document.documentElement.lang = normalizedCode;
    } else {
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = normalizedCode;
    }
  }, []);

  // Set initial document direction
  useEffect(() => {
    const langConfig = LANGUAGES[language];
    document.documentElement.dir = langConfig?.rtl ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  // Get language display info
  const getLanguageInfo = useCallback(() => {
    return LANGUAGES[language] || LANGUAGES.en;
  }, [language]);

  // Check if current language is RTL
  const isRTL = LANGUAGES[language]?.rtl || false;

  const value = {
    language,
    setLanguage,
    t,
    isRTL,
    getLanguageInfo,
    // Expose raw translations for advanced use cases
    translations: currentTranslations,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * useTranslation Hook
 * Primary hook for accessing translations in components
 * 
 * @example
 * const { t, language, setLanguage } = useTranslation();
 * return <h1>{t('landing.hero.title')}</h1>;
 * 
 * @example with interpolation
 * const { t } = useTranslation();
 * return <p>{t('dashboard.welcome', { name: 'John' })}</p>;
 */
export function useTranslation() {
  const context = useContext(LanguageContext);
  
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  
  return context;
}

/**
 * withTranslation HOC
 * For class components that need translation
 */
export function withTranslation(Component) {
  return function WrappedComponent(props) {
    const translation = useTranslation();
    return <Component {...props} {...translation} />;
  };
}

// Export language codes for convenience
export const SUPPORTED_LANGUAGES = Object.keys(translations).filter(code => code !== 'zh');

export default LanguageProvider;
